import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  TablePagination,
  CircularProgress,
  MenuItem,
  Select,
  InputAdornment,
  Skeleton,
  Button,
  Switch,
  Fade,
  DialogContent,
  Dialog,
} from "@mui/material";
import { flushSync } from "react-dom";
import { BASE_URL } from "../constants";
import { toast } from "react-toastify";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BusinessIcon from "@mui/icons-material/Business";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import SaveIcon from "@mui/icons-material/Save";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { Edit } from "lucide-react";

const getToday = () => new Date().toISOString().split("T")[0];

const cell = {
  textAlign: "center",
  fontSize: 11,
  py: 0.5,
  borderRight: "1px solid #eee",
};
const boxBlue = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#e0f2fe",
  borderRight: "1px solid #e5e7eb",
  px: 1,
};

const textBlue = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#0369a1",
};

const bounceStyle = {
  animation: "bounce 1s infinite",
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-8px)" },
  },
};

const Accounting = () => {
  const cols = 11;
  const inputRefs = useRef([]);

  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(getToday());
  const [invoiceSearchInput, setInvoiceSearchInput] = useState("CI");
  const [searchType, setSearchType] = useState("invoice"); // default
  const [settlementType, setSettlementType] = useState("unsettled"); // default

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBill, setSelectedBill] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveStep, setSaveStep] = useState(0);
  const saveBtnRef = useRef(null);

  const steps = [
    { text: "Saving settlements...", icon: <SaveIcon /> },
    { text: "Posting vouchers...", icon: <CurrencyRupeeIcon /> },
    { text: "Finalizing transaction...", icon: <TuneIcon /> },
  ];

  const [rowState, setRowState] = useState({});
  const getRow = (id) => rowState[id] || {};

  const updateRow = (id, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: field === "note" ? value : Number(value) || 0,
      },
    }));
  };

  console.log("selectedBill ", selectedBill);

  const calculateRow = (inv) => {
    const row = getRow(inv._id);

    const adjusted =
      (row.adjDoctor || 0) + (row.adjCentre || 0) + (row.adjOthers || 0);

    const paid =
      (row.paidDoctor || 0) + (row.paidCentre || 0) + (row.paidOthers || 0);

    const collected = inv.amountCollected || 0;
    const invoiceValue = inv.totalAmount || 0;

    const due = invoiceValue - (collected + adjusted);

    const revenue = collected + due - paid;

    const payables =
      (row.payDoctor || 0) + (row.payCentre || 0) + (row.payOthers || 0);

    return {
      adjusted,
      paid,
      due,
      revenue,
      payables,
      collected,
      invoiceValue,
    };
  };

  const getErrors = (inv) => {
    const row = getRow(inv._id);
    const calc = calculateRow(inv);

    return {
      adjusted: calc.adjusted > inv.totalAmount - inv.amountCollected,

      paid: calc.paid > inv.amountCollected,

      payables: calc.payables > calc.revenue,

      // equation: calc.collected + calc.adjusted !== calc.invoiceValue,
    };
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  // ================= FETCH =================
  const fetchAllinvoices = async (
    pageNo = 0,
    date = "",
    searchType = "invoice",
    query = "",
    settlementType,
  ) => {
    setLoading(true);
    if (!date || !query || !searchType) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${BASE_URL}api/user/accounting/get-all-invoices?page=${pageNo}&limit=${rowsPerPage}&startDate=${date}&endDate=${date}&query=${query}&searchType=${searchType}&type=${settlementType}`,
        {
          headers: {
          },
        },
      );

      const data = await res.json();

      if (data.success) {
        setInvoices(data.invoices);
        setTotalCount(data.totalCount);
      }
    } catch {
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const [counts, setCounts] = useState({});
  const [countLoading, setCountLoading] = useState(false);

  const fetchInvoiceCounts = async (date = "") => {
    setCountLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}api/user/accounting/get-invoices-count?date=${date}`,
        {
          headers: {
          },
        },
      );

      const data = await res.json();

      if (data.success) {
        setCounts({
          total: data.totalInvoices,
          settled: data.settledInvoices,
          unsettled: data.unsettledInvoices,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCountLoading(false);
    }
  };

  useEffect(() => {
    fetchAllinvoices(
      page,
      selectedDate,
      searchType,
      invoiceSearchInput,
      settlementType,
    );
    fetchInvoiceCounts(selectedDate);
  }, [page, selectedDate, searchType, invoiceSearchInput]);

  useEffect(() => {
    fetchAllinvoices(
      page,
      selectedDate,
      searchType,
      invoiceSearchInput,
      settlementType,
    );
  }, [settlementType]);

  useEffect(() => {
    inputRefs.current[1]?.focus();
  }, [invoices]);

  // ================= KEYBOARD =================
  const handleKeyDown = (e, index) => {
    let nextIndex = null;

    const moveRight = () => {
      // Paid Others → Remarks
      if (index % cols === 6) {
        return index + 2;
      }
      // Remarks → Payables Doctor
      else if (index % cols === 8) {
        return index + 1;
      }
      return index + 1;
    };

    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (!e.target.value) e.target.value = 0;
        nextIndex = moveRight(); // 🔥 SAME AS RIGHT
        break;

      case "ArrowRight":
        nextIndex = moveRight();
        break;

      case "ArrowLeft":
        if (index % cols === 8) {
          nextIndex = index - 2;
        } else if (index % cols === 9) {
          nextIndex = index - 1;
        } else {
          nextIndex = index - 1;
        }
        break;

      case "ArrowDown":
        nextIndex = index + cols;
        break;

      case "ArrowUp":
        nextIndex = index - cols;
        break;

      default:
        return;
    }

    // inputRefs.current[nextIndex]?.focus();
    const nextInput = inputRefs.current[nextIndex];

    if (nextInput) {
      nextInput.focus();

      // 🔥 ensure selection after focus
      setTimeout(() => {
        nextInput.select();
      }, 0);
    }
  };

  const SkeletonCard = () => (
    <Paper
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: "14px",
        border: "1px solid #eee",
      }}
    >
      {/* TOP ROW */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton width={100} height={20} />
          <Skeleton width={120} height={20} />
          <Skeleton width={120} height={20} />
          <Skeleton width={120} height={20} />
        </Box>

        <Skeleton width={80} height={30} />
      </Box>

      {/* TABLE HEADER */}
      <Skeleton height={30} />

      {/* INPUT ROW */}
      <Grid container spacing={0}>
        {[...Array(10)].map((_, i) => (
          <Grid item xs key={i}>
            <Skeleton height={35} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();

        // 🔥 Force blur so last value commits
        const active = document.activeElement;
        if (active && typeof active.blur === "function") {
          active.blur();
        }

        // 🔥 Trigger actual button click
        setTimeout(() => {
          saveBtnRef.current?.click();
        }, 50);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSaveSettlement = async () => {
    let interval;
    try {
      if (!invoices || invoices.length === 0) return;

      const payload = [];

      for (const inv of invoices) {
        const row = getRow(inv._id);

        // 🔥 CHECK IF USER ENTERED ANY VALUE
        const hasValue =
          row.adjDoctor ||
          row.adjCentre ||
          row.adjOthers ||
          row.paidDoctor ||
          row.paidCentre ||
          row.paidOthers ||
          row.payDoctor ||
          row.payCentre ||
          row.payOthers ||
          (row.note && row.note.trim() !== "");

        // ❌ SKIP EMPTY ROWS
        if (!hasValue) continue;

        const calc = calculateRow(inv);
        const errors = getErrors(inv);

        // ❌ VALIDATE ONLY FILLED ROWS
        if (errors.adjusted || errors.paid || errors.payables) {
          toast.error(`Invalid data in invoice ${inv.invoiceId}`);
          return;
        }

        payload.push({
          invoiceId: inv._id,

          adjustedAmount: {
            doctor: row.adjDoctor || 0,
            centre: row.adjCentre || 0,
            others: row.adjOthers || 0,
          },

          paidAmount: {
            doctor: row.paidDoctor || 0,
            centre: row.paidCentre || 0,
            others: row.paidOthers || 0,
          },

          payables: {
            doctor: row.payDoctor || 0,
            centre: row.payCentre || 0,
            others: row.payOthers || 0,
          },

          note: (row.note || "").trim(),

          // 🔥 calculated fields
          due: calc.due,
          revenue: calc.revenue,
        });
      }
      // 🔥 PREVENT EMPTY API CALL
      if (payload.length === 0) {
        toast.error("No data entered");
        return;
      }

      setSaving(true);
      setSaveStep(0);

      interval = setInterval(() => {
        setSaveStep((prev) => (prev + 1) % 3);
      }, 1000);
      // ================= API CALL =================
      const res = await fetch(
        `${BASE_URL}api/user/accounting/save-settlement`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ settlements: payload }),
        },
      );

      const data = await res.json();

      if (data.success) {
        toast.success("Settlement saved successfully");
        fetchAllinvoices(
          0,
          selectedDate,
          searchType,
          invoiceSearchInput,
          settlementType,
        );
        fetchInvoiceCounts(selectedDate);
      } else {
        toast.error(data.message || "Failed to save");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving settlement");
    } finally {
      clearInterval(interval);
      setSaving(false);
    }
  };

  const getValue = (inv, field, fallback) => {
    const row = getRow(inv._id);

    if (row[field] !== undefined) return row[field];

    return fallback ?? "";
  };

  const [updating, setUpdating] = useState(false);
  const handleUpdateSettlement = async (inv) => {
    try {
      setUpdating(true);
      const row = getRow(inv._id);
      const calc = calculateRow(inv);

      const payload = {
        invoiceId: inv._id,

        adjustedAmount: {
          doctor: row.adjDoctor || 0,
          centre: row.adjCentre || 0,
          others: row.adjOthers || 0,
        },

        paidAmount: {
          doctor: row.paidDoctor || 0,
          centre: row.paidCentre || 0,
          others: row.paidOthers || 0,
        },

        payables: {
          doctor: row.payDoctor || 0,
          centre: row.payCentre || 0,
          others: row.payOthers || 0,
        },

        note: row.note || "",

        due: calc.due,
        revenue: calc.revenue,
      };

      const res = await fetch(
        `${BASE_URL}api/user/accounting/update-settlement`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.success) {
        toast.success("Updated successfully");
        setSelectedBill(null);

        fetchAllinvoices(
          page,
          selectedDate,
          searchType,
          invoiceSearchInput,
          settlementType,
        );
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating settlement");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box sx={{ background: "#f9fafb", minHeight: "100vh" }}>
      <Dialog
        open={!!selectedBill}
        onClose={() => setSelectedBill(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "12px",
              background:
                "linear-gradient(135deg, rgba(42,84,166,0.08), rgba(185,58,207,0.08))",
              border: "1px solid rgba(185,58,207,0.2)",
            }}
          >
            {/* TITLE */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: "#1e293b",
                mb: 1,
              }}
            >
              Update Settlement
            </Typography>

            {/* INFO ROW */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                fontSize: "0.75rem",
                color: "#475569",
              }}
            >
              <Box>
                <strong>Invoice:</strong> {selectedBill?.invoiceId}
              </Box>

              <Box>
                <strong>Patient:</strong> {selectedBill?.patientName}
              </Box>

              <Box>
                <strong>Doctor:</strong> {selectedBill?.doctorName}
              </Box>

              <Box>
                <strong>Centre:</strong> {selectedBill?.centerName}
              </Box>

              <Box
                sx={{
                  fontWeight: 700,
                  color: "#059669",
                }}
              >
                ₹ {selectedBill?.totalAmount}
              </Box>
            </Box>
          </Box>

          {selectedBill && (
            <Grid container spacing={2}>
              {/* ADJUSTED */}
              {["doctor", "centre", "others"].map((key) => (
                <Grid item xs={4} key={key}>
                  <TextField
                    label={`Adj ${capitalize(key)}`}
                    size="small"
                    fullWidth
                    value={getValue(
                      selectedBill,
                      `adj${capitalize(key)}`,
                      selectedBill.adjustedAmount?.[key],
                    )}
                    onChange={(e) =>
                      updateRow(
                        selectedBill._id,
                        `adj${capitalize(key)}`,
                        e.target.value,
                      )
                    }
                  />
                </Grid>
              ))}

              {/* PAID */}
              {["doctor", "centre", "others"].map((key) => (
                <Grid item xs={4} key={key}>
                  <TextField
                    label={`Paid ${capitalize(key)}`}
                    size="small"
                    fullWidth
                    value={getValue(
                      selectedBill,
                      `paid${capitalize(key)}`,
                      selectedBill.paidAmount?.[key],
                    )}
                    onChange={(e) =>
                      updateRow(
                        selectedBill._id,
                        `paid${capitalize(key)}`,
                        e.target.value,
                      )
                    }
                  />
                </Grid>
              ))}

              {/* PAYABLE */}
              {["doctor", "centre", "others"].map((key) => (
                <Grid item xs={4} key={key}>
                  <TextField
                    label={`Pay ${capitalize(key)}`}
                    size="small"
                    fullWidth
                    value={getValue(
                      selectedBill,
                      `pay${capitalize(key)}`,
                      selectedBill.payables?.[key],
                    )}
                    onChange={(e) =>
                      updateRow(
                        selectedBill._id,
                        `pay${capitalize(key)}`,
                        e.target.value,
                      )
                    }
                  />
                </Grid>
              ))}

              {/* NOTE */}
              <Grid item xs={12}>
                <TextField
                  label="Note"
                  fullWidth
                  size="small"
                  value={getValue(selectedBill, "note", selectedBill.note)}
                  onChange={(e) =>
                    updateRow(selectedBill._id, "note", e.target.value)
                  }
                />
              </Grid>

              {/* SAVE BUTTON */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 1,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                  }}
                  disabled={updating}
                  onClick={() => handleUpdateSettlement(selectedBill)}
                >
                  {updating ? "Updating Settlement..." : "Update Settlement"}
                </Button>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {/* 🔥 TOP BAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        {/* LEFT SECTION */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* DATE */}
          <TextField
            size="small"
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <CalendarTodayIcon
                  sx={{ fontSize: 18, color: "#6b7280", mr: 1 }}
                />
              ),
            }}
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#f9fafb",
              },
            }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            {/* 🔽 DROPDOWN */}
            <Select
              size="small"
              value={searchType}
              onChange={(e) => {
                setSearchType(e.target.value);
                setPage(0);
              }}
              sx={{
                minWidth: 140,
                borderRadius: "8px",
                background: "#f9fafb",
                fontSize: 13,
              }}
            >
              <MenuItem value="invoice">Invoice ID</MenuItem>
              <MenuItem value="patient">Patient</MenuItem>
              <MenuItem value="doctor">Doctor</MenuItem>
              <MenuItem value="centre">Centre</MenuItem>
            </Select>

            {/* 🔍 SEARCH INPUT */}
            <TextField
              size="small"
              placeholder={`Search ${searchType}...`}
              value={invoiceSearchInput}
              onChange={(e) => {
                setInvoiceSearchInput(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  background: "#f9fafb",
                },
              }}
            />
          </Box>
        </Box>

        {/* RIGHT SECTION */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {invoices?.length > 0 && (
            <>
              <TuneIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "auto",
                    padding: 0,
                  },
                }}
              />{" "}
            </>
          )}
        </Box>
      </Box>

      {/* 🔥 COUNTS DASHBOARD */}
      {countLoading ? (
        <Grid container spacing={2} mb={2}>
          {[1, 2, 3].map((_, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* ICON SKELETON */}
                <Skeleton variant="circular" width={40} height={40} />

                <Box sx={{ flex: 1 }}>
                  {/* TITLE */}
                  <Skeleton width="60%" height={14} />

                  {/* VALUE */}
                  <Skeleton width="40%" height={28} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} mb={2}>
          {/* TOTAL */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: 2,
                background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                border: "1px solid #93c5fd",
              }}
            >
              <Box
                sx={{
                  p: 1.2,
                  borderRadius: "50%",
                  background: "#3b82f6",
                  color: "#fff",
                }}
              >
                <ReceiptLongIcon />
              </Box>

              <Box>
                <Typography fontSize={12} color="#1e3a8a">
                  Total Invoices
                </Typography>
                <Typography fontSize={20} fontWeight={700} color="#1e40af">
                  {counts.total || 0}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* SETTLED */}
          <Grid item xs={12} md={4}>
            <Paper
              onClick={() => setSettlementType("settled")}
              sx={{
                p: 2,
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",

                background:
                  settlementType === "settled"
                    ? "linear-gradient(135deg, #bbf7d0, #86efac)"
                    : "#f3f4f6",

                border:
                  settlementType === "settled"
                    ? "2px solid #16a34a"
                    : "1px solid #e5e7eb",

                transition: "all 0.2s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: "50%",
                    background:
                      settlementType === "settled" ? "#16a34a" : "#d1d5db",
                    color: "#fff",
                    opacity: settlementType === "settled" ? 1 : 0.6,
                  }}
                >
                  <CurrencyRupeeIcon />
                </Box>

                <Box>
                  <Typography
                    fontSize={12}
                    color={settlementType === "settled" ? "#14532d" : "#9ca3af"}
                  >
                    Settled
                  </Typography>

                  <Typography
                    fontSize={20}
                    fontWeight={700}
                    color={settlementType === "settled" ? "#166534" : "#6b7280"}
                  >
                    {counts.settled || 0}
                  </Typography>
                </Box>
              </Box>

              <Switch
                checked={settlementType === "settled"}
                onChange={() => setSettlementType("settled")}
                onClick={(e) => e.stopPropagation()}
                color="success"
              />
            </Paper>
          </Grid>

          {/* UNSETTLED */}
          <Grid item xs={12} md={4}>
            <Paper
              onClick={() => setSettlementType("unsettled")}
              sx={{
                p: 2,
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",

                background:
                  settlementType === "unsettled"
                    ? "linear-gradient(135deg, #fecaca, #fca5a5)"
                    : "#f3f4f6",

                border:
                  settlementType === "unsettled"
                    ? "2px solid #dc2626"
                    : "1px solid #e5e7eb",

                transition: "all 0.2s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: "50%",
                    background:
                      settlementType === "unsettled" ? "#dc2626" : "#d1d5db",
                    color: "#fff",
                    opacity: settlementType === "unsettled" ? 1 : 0.6,
                  }}
                >
                  <HourglassEmptyIcon />
                </Box>

                <Box>
                  <Typography
                    fontSize={12}
                    color={
                      settlementType === "unsettled" ? "#7f1d1d" : "#9ca3af"
                    }
                  >
                    Unsettled
                  </Typography>

                  <Typography
                    fontSize={20}
                    fontWeight={700}
                    color={
                      settlementType === "unsettled" ? "#991b1b" : "#6b7280"
                    }
                  >
                    {counts.unsettled || 0}
                  </Typography>
                </Box>
              </Box>

              <Switch
                checked={settlementType === "unsettled"}
                onChange={() => setSettlementType("unsettled")}
                onClick={(e) => e.stopPropagation()}
                color="error"
              />
            </Paper>
          </Grid>
        </Grid>
      )}

      {invoices?.length === 0 && invoices !== null && !loading && (
        <Box
          sx={{
            mt: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#6b7280",
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 28, color: "#9ca3af" }} />
          </Box>

          {/* Title */}
          <Typography fontSize={16} fontWeight={600} color="#111827">
            No invoices found
          </Typography>

          {/* Subtitle */}
          <Typography fontSize={13} mt={0.5}>
            Try changing the date or search filter
          </Typography>
        </Box>
      )}

      {/* 🔥 LOADER */}
      {loading ? (
        <Box>
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </Box>
      ) : (
        invoices?.map((inv, rowIndex) => (
          <Paper
            key={inv._id}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: "14px",
              background:
                settlementType === "settled"
                  ? "linear-gradient(135deg, #dcfce7, #bbf7d0)"
                  : "linear-gradient(135deg, #fee2e2, #fecaca)",
              border:
                settlementType === "settled"
                  ? "2px solid #16a34a"
                  : "2px solid #dc2626",
            }}
          >
            {/* 🔥 TOP ROW (ALL INFO INLINE) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1.2,
              }}
            >
              {/* LEFT INFO */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {/* Invoice */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ReceiptLongIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                  <Typography fontWeight={600} fontSize="13px">
                    {inv.invoiceId}
                  </Typography>
                </Box>

                {/* Patient */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                  <Typography fontSize="12px">
                    {inv.patientName.toUpperCase()}
                  </Typography>
                </Box>

                {/* Doctor */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocalHospitalIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                  <Typography fontSize="12px">{inv.doctorName}</Typography>
                </Box>

                {/* Centre */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                  <Typography fontSize="12px">{inv.centerName}</Typography>
                </Box>
              </Box>

              {/* RIGHT INFO (AMOUNT) */}
              <Box
                sx={{
                  display: "flex",
                }}
              >
                {settlementType === "settled" && (
                  <Box
                    onClick={() => setSelectedBill(inv)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,

                      width: 32,
                      height: 32,
                      borderRadius: "8px",

                      cursor: "pointer",

                      background:
                        "linear-gradient(135deg, rgba(42,84,166,0.1), rgba(185,58,207,0.1))",

                      border: "1px solid rgba(185,58,207,0.2)",

                      transition: "all 0.25s ease",

                      "&:hover": {
                        background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(185,58,207,0.3)",

                        "& svg": {
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <Edit
                      sx={{
                        fontSize: 16,
                        color: "#2a54a6",
                        transition: "all 0.2s ease",
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                    px: 1.5,
                    py: 0.6,
                    borderRadius: "5px",
                    border: "1px solid #a7f3d0",
                    boxShadow: "0 2px 6px rgba(16,185,129,0.15)",
                  }}
                >
                  <CurrencyRupeeIcon
                    sx={{
                      fontSize: 16,
                      color: "#059669",
                      background: "#d1fae5",
                      borderRadius: "50%",
                      p: 0.4,
                    }}
                  />

                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    sx={{ color: "#065f46", letterSpacing: "0.3px" }}
                  >
                    {inv.totalAmount}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* 🔥 TABLE BELOW */}
            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "5px",
                overflow: "hidden",
              }}
            >
              {/* 🔥 HEADER */}
              <Grid container>
                <Grid
                  item
                  xs={1}
                  sx={{
                    bgcolor: "#059669",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Collected
                </Grid>

                <Grid
                  item
                  xs={2}
                  sx={{
                    bgcolor: "#7c3aed",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Adjusted
                </Grid>

                <Grid
                  item
                  xs={2}
                  sx={{
                    bgcolor: "#0ea5e9",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Paid
                </Grid>

                <Grid
                  item
                  xs={1}
                  sx={{
                    bgcolor: "#f97316",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Due
                </Grid>

                <Grid
                  item
                  xs={1}
                  sx={{
                    bgcolor: "#f59e0b",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Net
                </Grid>

                <Grid
                  item
                  xs={3}
                  sx={{
                    bgcolor: "#6b7280",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Remarks
                </Grid>

                <Grid
                  item
                  xs={2}
                  sx={{
                    bgcolor: "#ef4444",
                    color: "#fff",
                    textAlign: "center",
                    fontSize: "12px",
                    py: 0.7,
                  }}
                >
                  Payables
                </Grid>
              </Grid>

              {/* 🔥 SUB HEADER */}
              <Grid container sx={{ bgcolor: "#f3f4f6" }}>
                <Grid item xs={1} sx={cell}>
                  Amt
                </Grid>

                {/* Adjusted */}
                <Grid container item xs={2}>
                  {["Doctor", "Centre", "Others"].map((label) => (
                    <Grid item xs={4} sx={cell}>
                      {label}
                    </Grid>
                  ))}
                </Grid>

                {/* Paid */}
                <Grid container item xs={2}>
                  {["Doctor", "Centre", "Others"].map((label) => (
                    <Grid item xs={4} sx={cell}>
                      {label}
                    </Grid>
                  ))}
                </Grid>

                <Grid item xs={1} sx={cell}>
                  Amt
                </Grid>
                <Grid item xs={1} sx={cell}>
                  Revenue
                </Grid>
                <Grid item xs={3} sx={cell}>
                  Note
                </Grid>

                {/* Payables */}
                <Grid container item xs={2}>
                  {["Doctor", "Centre", "Others"].map((l) => (
                    <Grid item xs={4} sx={cell}>
                      {l}
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid container>
                {/* Collected */}
                <Grid item xs={1}>
                  <Box sx={boxBlue}>
                    <Typography sx={textBlue}>
                      ₹ {inv.amountCollected}
                    </Typography>
                  </Box>
                </Grid>

                {/* Adjusted */}
                <Grid container item xs={2}>
                  {["doctor", "centre", "others"].map((key, i) => {
                    const index = rowIndex * cols + (i + 1);

                    return (
                      <Grid item xs={4} key={key}>
                        <TextField
                          size="small"
                          fullWidth
                          // value={getRow(inv._id)[`adj${capitalize(key)}`] || ""}
                          value={getValue(
                            inv,
                            `adj${capitalize(key)}`,
                            inv.adjustedAmount?.[key],
                          )}
                          onChange={(e) =>
                            updateRow(
                              inv._id,
                              `adj${capitalize(key)}`,
                              e.target.value,
                            )
                          }
                          disabled={settlementType === "settled"}
                          inputRef={(el) => (inputRefs.current[index] = el)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            style: { textAlign: "center", fontSize: 11 },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              // background: "#faf5ff",
                              background: getErrors(inv).adjusted
                                ? "#ff7171"
                                : "#faf5ff",
                            },
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Paid */}
                <Grid container item xs={2}>
                  {["doctor", "centre", "others"].map((key, i) => {
                    const index = rowIndex * cols + (i + 4);

                    return (
                      <Grid item xs={4} key={key}>
                        <TextField
                          size="small"
                          fullWidth
                          value={getValue(
                            inv,
                            `paid${capitalize(key)}`,
                            inv.paidAmount?.[key],
                          )}
                          onChange={(e) =>
                            updateRow(
                              inv._id,
                              `paid${capitalize(key)}`,
                              e.target.value,
                            )
                          }
                          disabled={settlementType === "settled"}
                          inputRef={(el) => (inputRefs.current[index] = el)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            style: { textAlign: "center", fontSize: 11 },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              // background: "#ecfdf5",
                              background: getErrors(inv).paid
                                ? "#ff7171"
                                : "#ecfdf5",
                            },
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Amount Due */}
                <Grid item xs={1}>
                  <Box sx={{ ...boxBlue, background: "#ffc4b0" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                      ₹ {calculateRow(inv).due}
                    </Typography>
                  </Box>
                </Grid>

                {/* Net */}
                <Grid item xs={1}>
                  <Box sx={{ ...boxBlue, background: "#fef3c7" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                      ₹ {calculateRow(inv).revenue}
                    </Typography>
                  </Box>
                </Grid>

                {/* Remarks */}
                <Grid item xs={3}>
                  <TextField
                    size="small"
                    fullWidth
                    value={getValue(inv, "note", inv.note)}
                    onChange={(e) => updateRow(inv._id, "note", e.target.value)}
                    inputRef={(el) =>
                      (inputRefs.current[rowIndex * cols + 8] = el)
                    }
                    disabled={settlementType === "settled"}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex * cols + 8)}
                    inputProps={{ style: { fontSize: 11 } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        background: "#f9fafb",
                      },
                    }}
                  />
                </Grid>

                {/* Payables */}
                <Grid container item xs={2}>
                  {["doctor", "centre", "others"].map((key, i) => {
                    const index = rowIndex * cols + (i + 9);

                    return (
                      <Grid item xs={4} key={key}>
                        <TextField
                          size="small"
                          fullWidth
                          value={getValue(
                            inv,
                            `pay${capitalize(key)}`,
                            inv.payables?.[key],
                          )}
                          onChange={(e) =>
                            updateRow(
                              inv._id,
                              `pay${capitalize(key)}`,
                              e.target.value,
                            )
                          }
                          disabled={settlementType === "settled"}
                          inputRef={(el) => (inputRefs.current[index] = el)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            style: { textAlign: "center", fontSize: 10 },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              // background: "#fef2f2",
                              background: getErrors(inv).payables
                                ? "#ff7171"
                                : "#fef2f2",
                            },
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        ))
      )}

      {invoices?.length > 0 && settlementType === "unsettled" && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#f9fafb", // Tailwind gray-50
            borderTop: "1px solid #e5e7eb", // Tailwind gray-200
            boxShadow: "0 -2px 4px rgba(0, 0, 0, 0.05)",
            zIndex: 100,
            paddingLeft: { xs: "0px", sm: "245px" },
            overflowX: { xs: "auto", md: "visible" }, // ✅ Enable scroll on small screens
            whiteSpace: "nowrap", // ✅ Prevent wrapping
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              minWidth: "900px",
            }}
          >
            <Button
              ref={saveBtnRef}
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              sx={{ borderRadius: 0 }}
              onClick={handleSaveSettlement}
            >
              Save Settlement / Ctrl+S
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={saving}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            px: 4,
            py: 3,
            textAlign: "center",
            minWidth: 320,
          },
        }}
        TransitionComponent={Fade}
      >
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            {/* ICON */}
            <Box
              sx={{
                fontSize: 40,
                color: "#16a34a",
                ...bounceStyle,
              }}
            >
              {steps[saveStep]?.icon}
            </Box>

            {/* MESSAGE */}
            <Typography fontWeight={600} fontSize={16}>
              {steps[saveStep]?.text}
            </Typography>

            {/* SUB TEXT */}
            <Typography fontSize={12} color="#6b7280">
              Please wait, this may take a few seconds...
            </Typography>

            {/* PROGRESS DOTS */}
            <Box display="flex" gap={1} mt={1}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: i === saveStep ? "#16a34a" : "#d1d5db",
                    transition: "all 0.3s",
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Accounting;
